import { Component, OnInit, inject } from '@angular/core';
import { Transition } from '@uirouter/core';
import { Store } from '@ngrx/store';
import { first, tap } from 'rxjs/operators';
import { IdentityService, SessionService } from 'account-hybrid/core/authentication';
import { LicenseType } from 'account-hybrid/core/authentication/models/licenses';
import { AccountMode, LicenseService } from 'account-hybrid/core/license.service';
import { SpacesService, getSpaceAttributesFromSpace } from 'account-hybrid/common/components/spaces';
import * as fromStore from 'account-hybrid/store/reducers/index';
import { GuestsV1ApiService } from 'apps/account/src/account-shared/services/guests-v1-api.service';
import {
  REDIRECT_SPINNER_TEMPLATE,
  buildSpaces,
  findCompanyOrMixedSpace,
  findPropertyOrMixedSpace,
  getPathForSpace,
} from './redirect-space.util';

/**
 * WS4 inc 5 (Track A) — native Angular redirect components replacing the AngularJS
 * global UIRouter redirect states (`account-v2/.../global/states/*.state.config.ts`).
 * Each is a pure-redirect, zero-UI leaf state: it shows the v1 loader spinner, resolves
 * the target space from the session, and does a full-page `location.href` navigation —
 * VERBATIM behaviour. Params come from the UIRouter `Transition` (the OauthResultViewComponent
 * precedent); no `resolve:` blocks needed. SessionService is the native Angular service.
 * Registered in `GLOBAL_STATES` (states.config.ts) + declared in CommonModule.
 *
 * These are deep-link entry points (email links) → cross-space full-page redirects, so they
 * are NOT pixel-gateable; correctness is the computed `location.href`, verified by a
 * non-mutating redirect-target diag (candidate vs frozen-v1 land identically).
 */

// curation-all: /{propertySlug}/curation/{companyCode} → <space>/stories/curation
@Component({
    selector: 'ft-curation-all-redirect', template: REDIRECT_SPINNER_TEMPLATE,
    standalone: false
})
export class CurationAllRedirectComponent implements OnInit {
  constructor(private transition: Transition, private session: SessionService) {}
  ngOnInit(): void {
    const { companyCode, propertySlug } = this.transition.params();
    const space = findPropertyOrMixedSpace(buildSpaces(this.session), companyCode, propertySlug);
    location.href = space ? `${getPathForSpace(space)}/stories/curation` : '/home';
  }
}

// feedback-all: /{propertySlug}/feedback/{companyCode} → <space>/people/feedback
@Component({
    selector: 'ft-feedback-all-redirect', template: REDIRECT_SPINNER_TEMPLATE,
    standalone: false
})
export class FeedbackAllRedirectComponent implements OnInit {
  constructor(private transition: Transition, private session: SessionService) {}
  ngOnInit(): void {
    const { companyCode, propertySlug } = this.transition.params();
    const space = findPropertyOrMixedSpace(buildSpaces(this.session), companyCode, propertySlug);
    location.href = space ? `${getPathForSpace(space)}/people/feedback` : '/home';
  }
}

// recommendation: /recommendation/{companyCode}/{propertySlug}/{recommendationUUID}
//                 → <space>/stories/library?recommendationUUID=<uuid>
@Component({
    selector: 'ft-recommendation-redirect', template: REDIRECT_SPINNER_TEMPLATE,
    standalone: false
})
export class RecommendationRedirectComponent implements OnInit {
  constructor(private transition: Transition, private session: SessionService) {}
  ngOnInit(): void {
    const { companyCode, propertySlug, recommendationUUID } = this.transition.params();
    const space = findPropertyOrMixedSpace(buildSpaces(this.session), companyCode, propertySlug);
    location.href = space ? `${getPathForSpace(space)}/stories/library?recommendationUUID=${recommendationUUID}` : '/home';
  }
}

// invitation: /invitation/{companyCode} → <space>
@Component({
    selector: 'ft-invitation-redirect', template: REDIRECT_SPINNER_TEMPLATE,
    standalone: false
})
export class InvitationRedirectComponent implements OnInit {
  constructor(private transition: Transition, private session: SessionService) {}
  ngOnInit(): void {
    const { companyCode } = this.transition.params();
    const space = findCompanyOrMixedSpace(buildSpaces(this.session), companyCode);
    location.href = space ? `${getPathForSpace(space)}` : '/home';
  }
}

// --- A.2: the 2 ASYNC redirect states (resolve the property from the guest first). WS5 #10
// Stage A inc 2d: `Guests.details` now resolves from the native `GuestsV1ApiService` (HttpClient
// port of account-v1/src/core/api/guests.js) instead of the $injector bridge — same URL,
// same response body. PRESERVE-BUGS: if details() rejects, the .then never fires → the spinner
// stays forever (the v1 behaviour); no .catch is added.

// timeline: /timeline/{companyCode}/{guestUUID} → <space>/people/library/{guestUUID}
@Component({
    selector: 'ft-timeline-redirect', template: REDIRECT_SPINNER_TEMPLATE,
    standalone: false
})
export class TimelineRedirectComponent implements OnInit {
  private readonly guests = inject(GuestsV1ApiService);
  constructor(
    private transition: Transition,
    private session: SessionService,
  ) {}
  ngOnInit(): void {
    const { companyCode, guestUUID } = this.transition.params();
    this.guests.details(guestUUID).then((details: any) => {
      const space = findPropertyOrMixedSpace(buildSpaces(this.session), companyCode, details.propertySlug);
      location.href = space ? `${getPathForSpace(space)}/people/library/${guestUUID}` : '/home';
    });
  }
}

// curate-guest: /curation/{companyCode}/{guestUUID} → <space>/stories/curation?advocateUUID={guestUUID}
@Component({
    selector: 'ft-curate-guest-redirect', template: REDIRECT_SPINNER_TEMPLATE,
    standalone: false
})
export class CurateGuestRedirectComponent implements OnInit {
  private readonly guests = inject(GuestsV1ApiService);
  constructor(
    private transition: Transition,
    private session: SessionService,
  ) {}
  ngOnInit(): void {
    const { companyCode, guestUUID } = this.transition.params();
    this.guests.details(guestUUID).then((details: any) => {
      const space = findPropertyOrMixedSpace(buildSpaces(this.session), companyCode, details.propertySlug);
      location.href = space ? `${getPathForSpace(space)}/stories/curation?advocateUUID=${guestUUID}` : '/home';
    });
  }
}

// --- home: the post-login entry point (the only IN-APP, partly pixel-gateable state). VERBATIM port
// of home.state.config.ts's 4-branch controller. Empty template (the spaces-selector renders from the
// app shell when isVisible is dispatched). The anonymous branch is PRE-EMPTED by the AngularJS
// AuthenticationCheckHook ($transitions.onBefore for requiresAuth states — applies to this Angular state
// too via the shared hybrid UIRouter), so it never runs here, but it's kept verbatim. redirectTo uses the
// UIRouter UrlService (the in-app $location.url equivalent); hub uses StateService.go; the spaces-selector
// dispatches the SAME raw ngrx actions to the SAME store → the picker renders identically.
@Component({
    selector: 'ft-home-redirect', template: '',
    standalone: false
})
export class HomeRedirectComponent implements OnInit {
  private readonly licenseService = inject(LicenseService);
  constructor(
    private transition: Transition,
    private identity: IdentityService,
    private spaces: SpacesService,
    private store: Store,
  ) {}
  ngOnInit(): void {
    const redirectTo = this.transition.params().redirectTo;
    const router = this.transition.router;
    if (this.identity.isAnonymous()) {
      router.stateService.go('sign-in', { redirectTo: window.location.pathname });
    } else if (!!redirectTo) {
      router.urlService.url(redirectTo);
    } else if (!this.spaces.current) {
      this.store
        .select(fromStore.selectSpaces)
        .pipe(
          first(),
          tap((store: any) => {
            if (store.allSpaces.length === 1 && store.collaborativeSpaces.length === 0) {
              document.location.href = getPathForSpace(store.allSpaces[0]);
            } else {
              this.store.dispatch({ type: '[Spaces] Set isCancelVisible', payload: false } as any);
              this.store.dispatch({ type: '[Spaces] Set isVisible', payload: true } as any);
            }
          }),
        )
        .subscribe();
    } else {
      // 1.73.x (ported from master's home.state.config.ts): full-access users land on hub;
      // restricted (SpacetimeOnly) users go to their licensed Spacetime surface, or the
      // spaces selector when they have multiple spaces / no Spacetime license.
      const isFullAccess = this.identity.isFliptoStaff() || this.licenseService.getAccountMode() === AccountMode.Full;
      if (isFullAccess) {
        router.stateService.go('hub', getSpaceAttributesFromSpace(this.spaces.current));
      } else if (this.licenseService.checkCurrentSpaceLicense(LicenseType.Spacetime)) {
        this.store
          .select(fromStore.selectSpaces)
          .pipe(first())
          .subscribe((store: any) => {
            const hasMultipleSpaces = store.allSpaces.length > 1 || store.collaborativeSpaces.length > 0;
            if (hasMultipleSpaces) {
              this.store.dispatch({ type: '[Spaces] Set isCancelVisible', payload: false } as any);
              this.store.dispatch({ type: '[Spaces] Set isVisible', payload: true } as any);
            } else {
              // The spacetime state lands on synopsis; the insight-status hook moves a locked one along.
              router.stateService.go('spacetime', getSpaceAttributesFromSpace(this.spaces.current));
            }
          });
      } else {
        this.store.dispatch({ type: '[Spaces] Set isCancelVisible', payload: false } as any);
        this.store.dispatch({ type: '[Spaces] Set isVisible', payload: true } as any);
      }
    }
  }
}
